import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { MapPin, Search, X, Check, Navigation, Crosshair, ZoomIn, ZoomOut, Locate, Info, MousePointer, Move } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Import Leaflet CSS
import "leaflet/dist/leaflet.css";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom red marker icon
const redMarkerIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Choluteca default coordinates
const CHOLUTECA_CENTER = { lat: 13.3006, lng: -87.1908 };
const DEFAULT_ZOOM = 14;

// Common locations in Choluteca area for quick selection
const COMMON_LOCATIONS = [
  { name: "Centro de Choluteca", lat: 13.3006, lng: -87.1908 },
  { name: "Parque Central", lat: 13.3012, lng: -87.1903 },
  { name: "Catedral de Choluteca", lat: 13.3008, lng: -87.1905 },
  { name: "Puente de Choluteca", lat: 13.2985, lng: -87.1875 },
  { name: "Mercado Municipal", lat: 13.3020, lng: -87.1910 },
  { name: "Terminal de Buses", lat: 13.3045, lng: -87.1950 },
  { name: "Hospital del Sur", lat: 13.3100, lng: -87.1850 },
  { name: "UNAH Choluteca", lat: 13.2950, lng: -87.1800 },
  { name: "Marcovia", lat: 13.2833, lng: -87.3167 },
  { name: "Namasigüe", lat: 13.2000, lng: -87.1000 },
  { name: "El Triunfo", lat: 13.2500, lng: -87.0833 },
  { name: "San Marcos de Colón", lat: 13.4167, lng: -86.8167 },
];

// Component to handle map click events
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to center map on coordinates
function MapCenterControl({ lat, lng, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], zoom || map.getZoom());
    }
  }, [lat, lng, zoom, map]);
  
  return null;
}

// Map with Leaflet
function InteractiveMap({ lat, lng, onChange, zoom = DEFAULT_ZOOM }) {
  const [mapCenter, setMapCenter] = useState([lat || CHOLUTECA_CENTER.lat, lng || CHOLUTECA_CENTER.lng]);
  const mapRef = useRef(null);

  const handleLocationSelect = (newLat, newLng) => {
    onChange(parseFloat(newLat.toFixed(6)), parseFloat(newLng.toFixed(6)));
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          onChange(parseFloat(latitude.toFixed(6)), parseFloat(longitude.toFixed(6)));
          if (mapRef.current) {
            mapRef.current.setView([latitude, longitude], 16);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("No se pudo obtener tu ubicación. Verifica los permisos del navegador.");
        }
      );
    }
  };

  return (
    <div className="relative">
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        ref={mapRef}
        style={{ height: "350px", width: "100%", borderRadius: "0.5rem" }}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        dragging={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapClickHandler onLocationSelect={handleLocationSelect} />
        
        {lat && lng && (
          <>
            <Marker position={[lat, lng]} icon={redMarkerIcon} />
            <MapCenterControl lat={lat} lng={lng} />
          </>
        )}
      </MapContainer>

      {/* Locate me button */}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="absolute top-2 right-2 z-[1000] shadow-lg"
        onClick={handleLocateMe}
        title="Usar mi ubicación actual"
      >
        <Locate className="h-4 w-4 mr-1" />
        Mi ubicación
      </Button>

      {/* Instructions legend */}
      <div className="absolute bottom-2 left-2 right-2 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
        <div className="font-semibold text-gray-800 mb-2 flex items-center gap-1">
          <Info className="h-3 w-3" />
          Cómo usar el mapa:
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
          <div className="flex items-center gap-1">
            <MousePointer className="h-3 w-3 text-blue-500" />
            <span>Clic = seleccionar punto</span>
          </div>
          <div className="flex items-center gap-1">
            <Move className="h-3 w-3 text-green-500" />
            <span>Arrastrar = mover mapa</span>
          </div>
          <div className="flex items-center gap-1">
            <ZoomIn className="h-3 w-3 text-purple-500" />
            <span>Rueda/+- = zoom</span>
          </div>
          <div className="flex items-center gap-1">
            <Locate className="h-3 w-3 text-orange-500" />
            <span>Botón = mi ubicación</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Location Picker Dialog V2
export function LocationPickerDialog({ isOpen, onClose, lat, lng, onSave, title = "Seleccionar Ubicación" }) {
  const [selectedLat, setSelectedLat] = useState(lat || CHOLUTECA_CENTER.lat);
  const [selectedLng, setSelectedLng] = useState(lng || CHOLUTECA_CENTER.lng);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedLat(lat || CHOLUTECA_CENTER.lat);
      setSelectedLng(lng || CHOLUTECA_CENTER.lng);
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [isOpen, lat, lng]);

  const handleMapChange = (newLat, newLng) => {
    setSelectedLat(newLat);
    setSelectedLng(newLng);
  };

  const handleQuickSelect = (location) => {
    setSelectedLat(location.lat);
    setSelectedLng(location.lng);
  };

  const handleSave = () => {
    onSave(selectedLat, selectedLng);
    onClose();
  };

  // Search using Nominatim (OpenStreetMap geocoding)
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}, Choluteca, Honduras&limit=5`
      );
      const data = await response.json();
      setSearchResults(data.map(item => ({
        name: item.display_name.split(',').slice(0, 3).join(','),
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon)
      })));
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const filteredLocations = searchQuery && !searchResults.length
    ? COMMON_LOCATIONS.filter(loc => 
        loc.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : COMMON_LOCATIONS;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-red-500" />
            {title}
            <span className="text-xs font-normal text-gray-500 ml-2">v2.0</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search bar */}
          <div>
            <Label className="mb-2 block">Buscar dirección</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Ej: Barrio El Centro, Calle Principal..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                />
              </div>
              <Button 
                type="button" 
                onClick={handleSearch}
                disabled={isSearching}
              >
                {isSearching ? "Buscando..." : "Buscar"}
              </Button>
            </div>
            
            {/* Search results */}
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-1">
                {searchResults.map((result, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      handleQuickSelect(result);
                      setSearchResults([]);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-800"
                  >
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{result.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Interactive Map */}
          <div>
            <Label className="mb-2 block">Selecciona en el mapa</Label>
            <InteractiveMap
              lat={selectedLat}
              lng={selectedLng}
              onChange={handleMapChange}
            />
          </div>

          {/* Coordinates display */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitud</Label>
              <Input
                type="number"
                step="0.000001"
                value={selectedLat}
                onChange={(e) => setSelectedLat(parseFloat(e.target.value) || 0)}
                className="font-mono text-sm"
              />
            </div>
            <div>
              <Label>Longitud</Label>
              <Input
                type="number"
                step="0.000001"
                value={selectedLng}
                onChange={(e) => setSelectedLng(parseFloat(e.target.value) || 0)}
                className="font-mono text-sm"
              />
            </div>
          </div>

          {/* Quick locations */}
          <div>
            <Label className="mb-2 block">Ubicaciones frecuentes</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
              {filteredLocations.map((location) => (
                <button
                  key={location.name}
                  type="button"
                  onClick={() => handleQuickSelect(location)}
                  className={`flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition-colors ${
                    Math.abs(selectedLat - location.lat) < 0.0001 && Math.abs(selectedLng - location.lng) < 0.0001
                      ? "bg-red-500 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{location.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="gap-2 bg-red-500 hover:bg-red-600">
            <Check className="h-4 w-4" />
            Guardar Ubicación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact Location Input with picker button - V2
export function LocationInput({ lat, lng, onChange, label = "Ubicación en Mapa" }) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleSave = (newLat, newLng) => {
    onChange({ lat: newLat, lng: newLng });
  };

  const handleClear = () => {
    onChange({ lat: null, lng: null });
  };

  const hasLocation = lat && lng;

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {label}
        <span className="text-xs text-gray-400 font-normal">(v2 con zoom)</span>
      </Label>
      <div className="flex gap-2">
        {hasLocation ? (
          <>
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm">
              <MapPin className="h-4 w-4 text-green-600" />
              <span className="font-mono text-green-700">
                {lat.toFixed(4)}, {lng.toFixed(4)}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setIsPickerOpen(true)}
              title="Editar ubicación"
            >
              <Navigation className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleClear}
              title="Quitar ubicación"
              className="text-red-500 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsPickerOpen(true)}
            className="w-full justify-start gap-2 text-gray-500"
          >
            <Crosshair className="h-4 w-4" />
            Agregar ubicación en mapa
          </Button>
        )}
      </div>

      <LocationPickerDialog
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        lat={lat}
        lng={lng}
        onSave={handleSave}
      />
    </div>
  );
}

export default LocationInput;
